import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { deleteCookie } from "../../../lib/helpers";
import {
  PartnerSlice,
  FormPage,
  PartnerToast,
  IUploadFileResponse,
  Project,
  IsNext,
  Cloudinary,
  BrandImage,
  AllTags,
  User,
  Steps,
  Warnings,
  ReadyToSubmit,
  CloudinaryImage,
  IntegrationsItem,
} from "../helpers/types";

const baseProject = {
  id: null,
  slug: "",
  project_id: null,
  project_name: "",
  address_one: "",
  address_two: "",
  city: "",
  state: "",
  status: "",
  submit_modal_displayed_at: "",
  zip_code: "",
  brands: [],
  project_locations: [],
  all_brand_tags: {
    cuisine: [],
    mood: [],
    other: [],
  },
  finance: [],
  warnings: {},
  onboarding_specialist_email_address: "",
  current_user: {
    email: "",
    is_inkind_admin: false,
    permission: "",
    editable_permissions: [],
    editable_brand_ids: [],
    available_projects: [],
    subscriber_id: null,
  },
  brands_editable: false,
  locations_editable: false,
  finances_editable: false,
  integrations_editable: false,
  project_locations_sorted: [],
  sections_metadata: {
    locations: {
      editable_count: 0,
      total_count: 0,
      preferred_navigation_id: null,
      all_editable: false,
      none_editable: true,
    },
    integrations: {
      editable_count: 0,
      total_count: 0,
      preferred_navigation_id: null,
      all_editable: false,
      none_editable: true,
    },
    brands: {
      editable_count: 0,
      total_count: 0,
      preferred_navigation_id: null,
      all_editable: false,
      none_editable: true,
    },
    finances: {
      editable_count: 0,
      total_count: 0,
      preferred_navigation_id: null,
      all_editable: false,
      none_editable: true,
    },
  },
};

const baseUser = {
  user: {
    id: null,
    token: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    birth_date: null,
    member_since: "",
    profile_picture: null,
    subscription: null,
    location: {
      address: null,
      city: null,
      state: null,
      zip_code: null,
      country: "",
      latitude: null,
      longitude: null,
    },
    details: {
      app_signup: false,
      ga_identifier: "",
      twitter_handle: null,
    },
    has_opted_out_email: null,
    has_opted_out_sms: false,
    do_not_track: false,
    email_verified_at: "",
    phone_verified_at: "",
    tip_calculation_base: "",
    default_tip_percent: null,
    subscription_level_id: null,
    is_freemium_subscriber: false,
    subscription_status: "",
    email_confirmed: false,
    referral: {
      give: null,
      get: null,
      min_spend: null,
    },
    is_app_installed: false,
    birthday: null,
    dietary_restrictions: [],
  },
};

//Initial state

const initialState: PartnerSlice = {
  project: baseProject,
  curPage: "Dashboard",
  curFormPage: 0,
  curFilter: "all",
  searchVal: "",
  cloudinary: {
    api_key: null,
    name: "",
    signature: "",
    timestamp: null,
    use_filename: null,
  },
  user: baseUser,
  loadingUser: "pending",
  partnerToast: {
    headline: "",
    subhead: "",
    additionalStyling: "",
  },
  imageList: [],
  tagsList: {
    cuisine: [],
    mood: [],
    other: [],
  },
  stripeUrl: "",
  warnings: {},
  status: "in_progress",
  submitModalDisplayed: false,
  steps: {
    brands: {},
    locations: {},
    finances: {},
    integrations: {},
  },
  contactFormModal: false,
  integrations: [],
  websites: [],
  isNext: {
    type: "",
    id: 0,
    step: "",
  },
  readyToSubmit: {
    locations: false,
    brands: false,
    finance: false,
  },
  leadId: null,
};

// Create reducers/actions

export const partnerSlice = createSlice({
  name: "partner",
  initialState,
  reducers: {
    setProject: (state, action: PayloadAction<Project>) => {
      state.project = action.payload;
    },
    setCurPage: (state, action: PayloadAction<string>) => {
      state.curPage = action.payload;
    },
    setLoadingUser: (state, action: PayloadAction<string>) => {
      state.loadingUser = action.payload;
    },
    setCurFormPage: (state, action: PayloadAction<FormPage>) => {
      state.curFormPage = action.payload;
    },
    setCurFilter: (state, action: PayloadAction<"all" | boolean>) => {
      state.curFilter = action.payload;
    },
    setSearchVal: (state, action: PayloadAction<string>) => {
      state.searchVal = action.payload;
    },
    setCloudinary: (state, action: PayloadAction<Cloudinary>) => {
      state.cloudinary = action.payload;
    },
    setImageList: (
      state,
      action: PayloadAction<
        BrandImage[] | CloudinaryImage[] | IUploadFileResponse[]
      >,
    ) => {
      state.imageList = action.payload;
    },
    setTagsList: (state, action: PayloadAction<AllTags>) => {
      state.tagsList = action.payload;
    },
    setPartnerToast: (state, action: PayloadAction<PartnerToast>) => {
      state.partnerToast = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setStripeUrl: (state, action: PayloadAction<string>) => {
      state.stripeUrl = action.payload;
    },
    setIntegrations: (state, action: PayloadAction<IntegrationsItem[]>) => {
      state.integrations = action.payload;
    },
    setWebsites: (state, action: PayloadAction<IntegrationsItem[]>) => {
      state.websites = action.payload;
    },
    setWarnings: (state, action: PayloadAction<Warnings>) => {
      state.warnings = action.payload;
    },
    setStatus: (
      state,
      action: PayloadAction<"in_review" | "in_progress" | "approved">,
    ) => {
      state.status = action.payload;
    },
    setSubmitModalDisplayed: (state, action: PayloadAction<string>) => {
      state.submitModalDisplayed = action.payload;
    },
    setReadyToSubmit: (state, action: PayloadAction<ReadyToSubmit>) => {
      state.readyToSubmit = action.payload;
    },
    setSteps: (state, action: PayloadAction<Steps>) => {
      state.steps = action.payload;
    },
    setIsNext: (state, action: PayloadAction<IsNext>) => {
      state.isNext = action.payload;
    },
    setContactFormModal: (state, action: PayloadAction<boolean>) => {
      state.contactFormModal = action.payload;
    },
    setLeadId: (state, action: PayloadAction<number | null>) => {
      state.leadId = action.payload;
    },
    logOutUser: (state) => {
      state.user = baseUser;
      state.project = baseProject;
      state.curFilter = "all";
      state.searchVal = "";
      state.imageList = [];
      state.tagsList = {
        cuisine: [],
        mood: [],
        other: [],
      };
      state.stripeUrl = "";
      state.warnings = {};
      state.status = "in_progress";
      state.curFormPage = 0;
      state.imageList = [];
      state.steps = {
        brands: {},
        locations: {},
        finances: {},
        integrations: {},
      };
      state.contactFormModal = false;
      state.isNext = {
        type: "",
        id: 0,
        step: "",
      };
      state.readyToSubmit = {
        locations: false,
        brands: false,
        finance: false,
      };
      state.partnerToast = {
        headline: "",
        subhead: "",
        additionalStyling: "",
      };
      state.submitModalDisplayed = false;
      state.contactFormModal = false;
      deleteCookie();
    },
  },
});

// Export actions

export const {
  setProject,
  setCurPage,
  setCurFormPage,
  setCurFilter,
  setSearchVal,
  setCloudinary,
  setImageList,
  setTagsList,
  setPartnerToast,
  setStripeUrl,
  setWarnings,
  setUser,
  setStatus,
  setReadyToSubmit,
  setSubmitModalDisplayed,
  setSteps,
  setIsNext,
  setContactFormModal,
  setIntegrations,
  setWebsites,
  logOutUser,
  setLoadingUser,
  setLeadId,
} = partnerSlice.actions;

export default partnerSlice.reducer;
